<template>
  <div class="team">
    <div class="ob-col">
      <div class="ob-head">
        <span class="ob-head__icon" style="background: rgba(59, 130, 246, 0.1); color: #3b82f6"><span class="mdi mdi-account-multiple-outline"></span></span>
        <h2 class="ob-head__title">¿Quieres invitar a tu equipo?</h2>
        <p class="ob-head__sub">Puedes agregar técnicos y colaboradores ahora o hacerlo después desde Configuración</p>
      </div>

      <transition-group name="row" tag="div">
        <div v-for="(row, i) in ob.team" :key="row.key" class="trow">
          <div class="trow__fields">
            <input v-model="row.name" class="ob-input" placeholder="Nombre" maxlength="80" :disabled="row.status === 'sent'" :aria-label="`Nombre de la persona ${i + 1}`" />
            <input v-model="row.email" type="email" class="ob-input" placeholder="correo@empresa.com" :disabled="row.status === 'sent'" :aria-label="`Correo de la persona ${i + 1}`" />
            <select v-model="row.role" class="ob-select" :disabled="row.status === 'sent'" :aria-label="`Rol de la persona ${i + 1}`">
              <option v-for="r in TEAM_ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
            </select>
            <span v-if="row.status === 'sent'" class="trow__ok mdi mdi-check-circle" title="Invitación registrada"></span>
            <button v-else type="button" class="trow__remove" :disabled="ob.team.length === 1" aria-label="Quitar esta persona" @click="remove(row)">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
          <p v-if="row.error" class="trow__error">{{ row.error }}</p>
        </div>
      </transition-group>

      <button type="button" class="ob-link" @click="ob.team.push(newTeamRow())">＋ Agregar otra persona</button>

      <p v-if="message" class="ob-error">{{ message }}</p>

      <div class="team__actions">
        <button type="button" class="ob-btn" :disabled="sending" @click="send">{{ sending ? 'Enviando…' : 'Enviar invitaciones' }}</button>
        <button type="button" class="ob-link ob-link--gray" :disabled="sending" @click="$emit('later')">Lo haré después</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ob, newTeamRow } from '@/services/onboardingStore'
import { inviteUser } from '@/services/users'
import { TEAM_ROLES, isValidEmail } from './onboardingFormat'

const emit = defineEmits(['later', 'done'])

const sending = ref(false)
const message = ref('')

function remove(row) {
  if (ob.team.length > 1) ob.team = ob.team.filter((r) => r.key !== row.key)
}

const ERRORS = { EMAIL_ALREADY_HAS_ACCESS: 'Este correo ya tiene acceso al sistema.' }

async function send() {
  message.value = ''
  const rows = ob.team.filter((r) => r.status !== 'sent' && (r.name.trim() || r.email.trim()))
  if (!rows.length) {
    // Nada que enviar: si ya se mandó alguna, se avanza; si no, se avisa.
    if (ob.team.some((r) => r.status === 'sent')) return emit('done')
    message.value = 'Escribe el correo de al menos una persona, o elige "Lo haré después".'
    return
  }
  let invalid = false
  for (const r of rows) {
    r.error = ''
    if (!isValidEmail(r.email)) { r.error = 'Escribe un correo válido.'; invalid = true }
  }
  if (invalid) return

  sending.value = true
  let failed = 0
  for (const r of rows) {
    try {
      await inviteUser(ob.parkId, { email: r.email.trim().toLowerCase(), role: r.role, name: r.name.trim() || undefined })
      r.status = 'sent'
    } catch (e) {
      failed++
      r.error = ERRORS[e.data?.error] || e.data?.message || 'No se pudo registrar esta invitación.'
    }
  }
  sending.value = false
  if (!failed) emit('done')
  else message.value = 'Algunas invitaciones no se pudieron registrar. Corrígelas o elige "Lo haré después".'
}
</script>

<style scoped>
.team { position: absolute; inset: 0; overflow: auto; display: flex; }
.trow { margin-bottom: 10px; }
.trow__fields { display: grid; grid-template-columns: 1fr 1.3fr 1fr 28px; gap: 8px; align-items: center; }
.trow .ob-input, .trow .ob-select { font-size: 13px; height: 40px; }
.trow__remove { height: 40px; width: 28px; border: none; background: none; color: #94a3b8; cursor: pointer; font-size: 16px; }
.trow__remove:hover:not(:disabled) { color: #e84545; }
.trow__remove:disabled { opacity: 0.3; cursor: default; }
.trow__ok { color: #10b981; font-size: 18px; text-align: center; }
.trow__error { margin: 4px 0 0; font: 400 11px Inter, sans-serif; color: #b91c1c; }
.team__actions { margin-top: 26px; display: flex; flex-direction: column; align-items: center; gap: 14px; }
.team__actions .ob-btn { width: 100%; }

.row-enter-active { transition: opacity 0.2s, transform 0.2s; }
.row-enter-from { opacity: 0; transform: translateY(-8px); }

@media (max-width: 640px) {
  .trow__fields { grid-template-columns: 1fr 1fr; }
  .trow__fields .ob-select { grid-column: 1 / 2; }
}
@media (prefers-reduced-motion: reduce) { .row-enter-active { transition: none; } }
</style>